import { esc, isDesktop } from "./dom.js";
import { db, rpc } from "./supabaseClient.js";
import { state } from "./state.js";
import { toast } from "./toast.js";
import { showState } from "./format.js";
import { renderAll, renderAdminOrSettings } from "./layout.js";
import { renderShows } from "../features/shows.js";
import { openShow } from "../features/picks.js";
import { renderBoard } from "../features/standings.js";
import { refreshSignupBadge } from "./switcher.js";

const myLastPts = {};
let channel = null;
let pingChannel = null;
let visListenerAttached = false;

// scores/league_shows have zero public RLS SELECT policies by design
// (scoped reads go through RPCs — see sql/stage_j_realtime_ping.sql) so
// their own postgres_changes events never deliver anything, silently. This
// is what actually reacts to a change in either table: the edge function
// upserts a `realtime_pings` row (nothing but {league_id, show_id,
// updated_at}) once per real change, the ping channel below delivers that,
// and this refetches through the same authenticated RPCs the rest of the
// app already uses — real data never has to cross the public ping
// channel. One get_bracket_scores call covers both the winner toast and
// the "my points" toast, rather than the two separate fetches the old
// (dead) per-table bindings used.
async function handlePing(showId){
  const [lsRows, shRes, sc] = await Promise.all([
    rpc("get_league_shows", { p_league_id: state.currentLeagueId }).catch(() => []),
    db.from("shows").select("*").eq("id", showId).single(),
    rpc("get_bracket_scores", { p_name:state.session.name, p_pin:state.session.pin, p_bracket_id:state.currentBracketId, p_show_id: showId }).catch(() => null),
  ]);
  const ls = (lsRows||[]).find(r => r.show_id === showId);
  const sh = shRes.data;
  const fresh = ts => ts && (Date.now() - new Date(ts).getTime()) < 3*60e3;

  if (sh && ls && fresh(ls.remind_sent)){
    let mine = [];
    try{ mine = await rpc("get_my_picks", { p_name:state.session.name, p_pin:state.session.pin, p_bracket_id:state.currentBracketId, p_show_id:sh.id }); }catch(e){}
    toast(mine.length
      ? `⏰ 1 hour to cutoff — ${esc(sh.venue||sh.showdate)}. Your picks are in ✔`
      : `⏰ 1 hour to cutoff — ${esc(sh.venue||sh.showdate)}. You haven't voted!`, "", `remind:${sh.id}`);
  }
  if (sh && ls && fresh(ls.lock_sent))
    toast(`\u{1F512} All picks locked — ${esc(sh.venue||sh.showdate)}. Boards are public.`, "", `lock:${sh.id}`);
  if (sh && ls && fresh(ls.winner_sent) && sc?.length){
    const top = sc.slice().sort((a,b) => b.points - a.points)[0];
    if (top) toast(`\u{1F3C6} ${esc(top.player_name||"?")} takes ${esc(sh.venue||sh.showdate)} with ${top.points} pts`, "score", `win:${sh.id}`);
  }
  const mineRow = (sc||[]).find(r => r.player_id === state.session?.id);
  if (mineRow && myLastPts[showId] !== mineRow.points){
    myLastPts[showId] = mineRow.points;
    toast(`You're at ${mineRow.points} pts for this show`, "score");
  }
  if (state.tab === "board") renderBoard();
}

// Teardown-and-rebuildable: called once at boot and again on every bracket
// switch, so it always tears down the previous subscription first — no
// stacking of stale channels for a bracket the player isn't looking at
// anymore. Two channels, deliberately kept separate — the ping table gets
// its own so a future misconfiguration there (added to the wrong
// publication, missing its policy, etc.) can't repeat the confirmed bug
// where one bad binding poisoned postgres_changes delivery for every OTHER
// binding sharing its channel (see the CLAUDE.md realtime gotcha).
export function subscribeRealtime(){
  if (channel) db.removeChannel(channel);
  channel = db.channel(`live-${state.currentBracketId}`)
    .on("postgres_changes", { event:"INSERT", schema:"public", table:"setlist_songs" }, p => {
      const s = p.new;
      // Debut detection is a footnote regex match (scoring.js/index.ts both
      // use the same /debut/i test), not a structured flag — matches the
      // Discord announcement's own "DEBUT 🥚" wording for consistency.
      toast(`🎵 ${esc(s.songname)}${s.is_encore ? " (encore)" : ""}${/debut/i.test(s.footnote||"") ? " — DEBUT 🥚" : ""}`, "", `song:${s.show_id}:${(s.songname||"").toLowerCase()}`);
      if (state.currentShow && state.tab !== "admin" && s.show_id === state.currentShow.id) openShow(state.currentShow.id);
    })
    // bracket_id is a real column on seasons — filters server-side rather
    // than subscribing unfiltered and checking client-side. league_shows
    // and scores used to have bindings here too, but neither ever
    // delivered anything (no public RLS policy — see handlePing above),
    // so they're gone from this channel, not just dead weight kept around.
    .on("postgres_changes", { event:"UPDATE", schema:"public", table:"seasons", filter:`bracket_id=eq.${state.currentBracketId}` }, p => {
      const se = p.new;
      if (se.winner_sent && (Date.now() - new Date(se.winner_sent).getTime()) < 3*60e3)
        toast(`\u{1F451} ${esc(se.name)} is in the books — check Standings for the podium`, "score", `season:${se.id}`);
    })
    // This class of failure is invisible by design otherwise: a channel
    // whose postgres_changes registration silently fails (e.g. subscribing
    // to a table absent from the publication — see the CLAUDE.md gotcha)
    // still reports SUBSCRIBED, since that status only reflects the
    // channel/socket join, not whether change events are actually being
    // delivered. A warning on any OTHER status is the only client-visible
    // signal something's wrong.
    .subscribe((status, err) => { if (status !== "SUBSCRIBED") console.warn("[realtime] channel status:", status, err || ""); });

  if (pingChannel) db.removeChannel(pingChannel);
  pingChannel = db.channel(`ping-${state.currentLeagueId}`)
    .on("postgres_changes", { event:"*", schema:"public", table:"realtime_pings", filter:`league_id=eq.${state.currentLeagueId}` }, p => {
      const showId = p.new?.show_id;
      if (showId != null) handlePing(showId);
    })
    .subscribe((status, err) => { if (status !== "SUBSCRIBED") console.warn("[realtime] ping channel status:", status, err || ""); });

  // Only ever attached once — subscribeRealtime() itself runs again on every
  // bracket switch, and a second document listener would fire refreshCurrent()
  // multiple times per visibility change.
  if (!visListenerAttached){
    visListenerAttached = true;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState !== "visible" || !state.session) return;
      // Vet-first badge (onboarding.md decision 7): one call per foreground.
      // When this pass re-renders the third column, its admin list load sets
      // the badge; otherwise the badge makes its own call.
      if (!refreshCurrent()) refreshSignupBadge();
    });
  }
}
// Returns true when this pass re-rendered the third column (Admin or
// Settings), whose admin list load also refreshes the sign-up badge.
export function refreshCurrent(){
  if (state.tab === "shows" && state.currentShow && showState(state.currentShow) === "open") return false; // never wipe an in-progress pick sheet
  if (isDesktop()){ renderAll(); return true; }
  if (state.tab === "board") renderBoard();
  // "admin" is the shared third-tab slot — Admin for league admins,
  // Settings for everyone else (same sentinel value either way, see
  // settings.js's renderSettings). Calling renderAdmin() directly here
  // instead of the role-aware dispatcher rendered the admin-only panel
  // (and its admin-gated RPC calls) for a NON-admin on the Settings tab —
  // happened to look correct in every admin-tested session since
  // isCurrentLeagueAdmin() made the two calls equivalent for an admin.
  else if (state.tab === "admin"){ renderAdminOrSettings(); return true; }
  else if (state.currentShow) openShow(state.currentShow.id);
  else renderShows();
  return false;
}
